import React, { useCallback, useEffect, useState } from 'react'
import styled from 'styled-components'

import HelpResourceRow from './HelpResourceRow'
import Spinner from 'components/Spinner'
import Table from 'components/Table'
import WistiaVideoRow from '../shared/WistiaVideoRow'
import ThermostatReleaseRow from './ThermostatReleaseRow'

import Resource, { ValidResourceTypes } from 'types/Resource'
import { color } from 'styles/colors'

type Props = {
  type: ValidResourceTypes
  resources: Array<Resource>
  isLoaded: boolean
  searchText: string
}

const ResourceTable = ({ type, resources, isLoaded = false, searchText }: Props) => {
  const [nameSortDirection, setNameSortDirection] = useState<'asc' | 'desc' | null>('desc')
  const [dateSortDirection, setDateSortDirection] = useState<'asc' | 'desc' | null>(null)
  const [sortedResources, setSortedResources] = useState<Array<Resource>>(resources)

  useEffect(() => {
    setSortedResources(resources)
  }, [resources])

  let content
  const hideVideoSpecificHeaders = false
  let hasThermostatSpecificHeaders = false

  switch (type) {
    case 'video':
      content = isLoaded ? sortedResources.map((video) => (
        <WistiaVideoRow video={video} key={video.hashedId} />
      )) : <></>

      break
    case 'article':
      content = isLoaded ? sortedResources.map((article) => (
        <HelpResourceRow resource={article} key={article.s3Key} />
      )) : <></>

      break
    case 'faq':
      content = isLoaded ? sortedResources.map((faq) => (
        <HelpResourceRow resource={faq} key={faq.s3Key} />
      )) : <></>

      break
    case 'thermostat':
      content = isLoaded ? resources.map((thermostat) => <ThermostatReleaseRow resource={thermostat} key={thermostat.id} />) : <></>
      hasThermostatSpecificHeaders = true
      break
    default:
      content = null
  }

  const onClickNameSortIcon = useCallback(() => {
    setDateSortDirection(null)
    setNameSortDirection(nameSortDirection === 'asc' ? 'desc' : 'asc')

    const resourcesSortedByName = resources.sort((a, b) => {
      if (nameSortDirection === 'asc') {
        return a.name.localeCompare(b.name)
      } else {
        return b.name.localeCompare(a.name)
      }
    })

    setSortedResources(resourcesSortedByName)
  }, [nameSortDirection, resources])

  const onClickDateSortIcon = useCallback(() => {
    setNameSortDirection(null)
    setDateSortDirection(dateSortDirection === 'asc' ? 'desc' : 'asc')

    const resourcesSortedByDate = resources.sort((a, b) => {
      const dateA = new Date((a.wistiaUpdatedAt || a.createdAt) ?? 0).getTime()
      const dateB = new Date((b.wistiaUpdatedAt || b.createdAt) ?? 0).getTime()

      if (dateSortDirection === 'asc') {
        return dateA - dateB
      } else {
        return dateB - dateA
      }
    })

    setSortedResources(resourcesSortedByDate)
  }, [dateSortDirection, resources])

  if (resources?.length === 0 && searchText !== '') {
    return (
      <S.NoResult>
        <S.NoDataText>No Results are found for '{searchText}'</S.NoDataText>
      </S.NoResult>
    )
  }

  return (
    <Table>
      <Table.Header>
        { hasThermostatSpecificHeaders &&
        <>
          <S.VersionHeaderCell title="Release Version" />
          <S.ModelHeaderCell title="Thermostat Model(s)" />
          <S.ReleaseDateHeaderCell title="Release Date" />
          <S.DescriptionHeaderCell title="Description" />
        </>
        }
        { !hasThermostatSpecificHeaders &&
          <>
            <S.NameHeaderCell
              title="Name"
              sortable
              sortDirection={nameSortDirection}
              onSort={onClickNameSortIcon}
            />
            <S.DescriptionHeaderCell title="Description" />
            {!hideVideoSpecificHeaders && <S.DateHeaderCell
              sortable
              sortDirection={dateSortDirection}
              onSort={onClickDateSortIcon}
              title="Date"
                                          />
            }
          </>
        }
      </Table.Header>

      {(!isLoaded) && <Spinner variant="page" />}
      {content}
    </Table>
  )
}

export default ResourceTable

const S = {
  DateHeaderCell: styled(Table.HeaderCell)`
    flex-basis: 25%;
  `,
  ModelHeaderCell: styled(Table.HeaderCell)`
    flex-basis: 15%;
  `,
  VersionHeaderCell: styled(Table.HeaderCell)`
    flex-basis: 15%;
  `,
  ReleaseDateHeaderCell: styled(Table.HeaderCell)`
    flex-basis: 20%;
  `,
  DescriptionHeaderCell: styled(Table.HeaderCell)`
    flex-basis: 50%;
  `,
  NameHeaderCell: styled(Table.HeaderCell)`
    flex-basis: 25%;
    min-width: 229px;
  `,
  NoResult: styled.div`
    display: flex;
    justify-content: center;
  `,
  NoDataText: styled.span`
    text-transform: uppercase;
    font-size: 25px;
    color: ${color('secondaryText')};
    margin: 10% 0%;
  `
}
